interface SuppressionListParams {
  search?: string
  page?: number
}

function buildQuery(params?: Record<string, string | number | boolean | undefined>): string {
  if (!params) return ''
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      searchParams.set(key, String(value))
    }
  }
  const qs = searchParams.toString()
  return qs ? `?${qs}` : ''
}

export const endpoints = {
  auth: {
    me: '/auth/session',
    login: '/auth/sign-in/email',
    logout: '/auth/sign-out',
    signup: '/auth/sign-up/email',
    socialSignIn: '/auth/sign-in/social',
  },

  account: {
    get: '/dashboard/account',
  },

  emails: {
    list: (params?: {
      page?: number
      per_page?: number
      status?: string
      search?: string
      domain?: string
      transport?: string
      from?: string
      to?: string
      api_key_id?: string
      type?: string
    }): string => `/v1/emails${buildQuery(params)}`,
    detail: (id: string): string => `/v1/emails/${id}`,
  },

  domains: {
    list: '/v1/domains',
    create: '/v1/domains',
    detail: (id: string): string => `/v1/domains/${id}`,
    verify: (id: string): string => `/v1/domains/${id}/verify`,
    delete: (id: string): string => `/v1/domains/${id}`,
  },

  analytics: (params?: {
    from?: string
    to?: string
    granularity?: string
    domain?: string
    tag?: string
    transport?: string
  }): string => `/v1/analytics${buildQuery(params)}`,

  apiKeys: {
    list: '/dashboard/api-keys',
    create: '/dashboard/api-keys',
    revoke: (id: string): string => `/dashboard/api-keys/${id}`,
    usage: '/dashboard/api-keys/usage',
  },

  webhooks: {
    list: '/v1/webhooks',
    create: '/v1/webhooks',
    update: (id: string): string => `/v1/webhooks/${id}`,
    delete: (id: string): string => `/v1/webhooks/${id}`,
    logs: (id: string): string => `/v1/webhooks/${id}/logs`,
    test: (id: string): string => `/v1/webhooks/${id}/test`,
  },

  suppressions: {
    list: (params?: SuppressionListParams): string =>
      `/v1/suppressions${buildQuery(params as Record<string, string | number | boolean | undefined>)}`,
    create: '/v1/suppressions',
    delete: (id: string): string => `/v1/suppressions/${id}`,
  },

  templates: {
    list: (params?: { page?: number; per_page?: number; search?: string }): string =>
      `/v1/templates${buildQuery(params)}`,
    get: (id: string): string => `/v1/templates/${id}`,
    create: '/v1/templates',
    update: (id: string): string => `/v1/templates/${id}`,
    publish: (id: string): string => `/v1/templates/${id}/publish`,
  },

  ips: {
    request: '/v1/ips/request',
  },

  notifications: {
    getSlack: '/v1/notifications/slack',
    saveSlack: '/v1/notifications/slack',
    testSlack: '/v1/notifications/slack/test',
    getThresholds: '/v1/notifications/thresholds',
    saveThresholds: '/v1/notifications/thresholds',
    getHistory: (params?: { page?: number; per_page?: number }): string =>
      `/v1/notifications/history${buildQuery(params)}`,
  },

  audiences: {
    list: (params?: { page?: number; per_page?: number }): string =>
      `/v1/audiences${buildQuery(params)}`,
    create: '/v1/audiences',
    detail: (id: string): string => `/v1/audiences/${id}`,
    update: (id: string): string => `/v1/audiences/${id}`,
    delete: (id: string): string => `/v1/audiences/${id}`,
    contacts: (
      id: string,
      params?: {
        page?: number
        per_page?: number
        search?: string
        status?: string
      },
    ): string => `/v1/audiences/${id}/contacts${buildQuery(params)}`,
    addContacts: (id: string): string => `/v1/audiences/${id}/contacts`,
    removeContacts: (id: string): string => `/v1/audiences/${id}/contacts`,
    sync: (id: string): string => `/v1/audiences/${id}/sync`,
  },

  campaigns: {
    list: (params?: { page?: number; per_page?: number; status?: string }): string =>
      `/v1/campaigns${buildQuery(params)}`,
    create: '/v1/campaigns',
    detail: (id: string): string => `/v1/campaigns/${id}`,
    update: (id: string): string => `/v1/campaigns/${id}`,
    delete: (id: string): string => `/v1/campaigns/${id}`,
    send: (id: string): string => `/v1/campaigns/${id}/send`,
    schedule: (id: string): string => `/v1/campaigns/${id}/schedule`,
    cancel: (id: string): string => `/v1/campaigns/${id}/cancel`,
    sendWinner: (id: string): string => `/v1/campaigns/${id}/send-winner`,
    recipients: (
      id: string,
      params?: {
        page?: number
        per_page?: number
        status?: string
      },
    ): string => `/v1/campaigns/${id}/recipients${buildQuery(params)}`,
    slots: (id: string): string => `/v1/campaigns/${id}/slots`,
  },

  bugReports: {
    list: '/v1/bug-reports',
    detail: (id: string): string => `/v1/bug-reports/${id}`,
    create: '/v1/bug-reports',
    uploadScreenshot: '/v1/bug-reports/screenshot',
  },

  wordpress: {
    connections: '/v1/wordpress/connections',
    createToken: '/v1/wordpress/tokens',
    revokeConnection: (id: string): string => `/v1/wordpress/connections/${id}`,
    articles: (params?: { page?: number; per_page?: number }): string =>
      `/v1/wordpress/articles${buildQuery(params)}`,
    deleteArticle: (id: string): string => `/v1/wordpress/articles/${id}`,
    groups: '/v1/wordpress/groups',
    groupDetail: (id: string): string => `/v1/wordpress/groups/${id}`,
    createGroup: '/v1/wordpress/groups',
    updateGroup: (id: string): string => `/v1/wordpress/groups/${id}`,
    deleteGroup: (id: string): string => `/v1/wordpress/groups/${id}`,
  },

  advertisers: (params?: { from?: string; to?: string; advertiser?: string }): string =>
    `/v1/analytics/advertisers${buildQuery(params)}`,

  analyticsExport: '/v1/analytics/export',

  billing: {
    checkout: '/dashboard/billing/checkout',
    portal: '/dashboard/billing/portal',
    usage: '/dashboard/billing/usage',
    acknowledgeOverage: '/dashboard/billing/acknowledge-overage',
  },
} as const
